import { Box, Button, Drawer, IconButton, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import Input from 'components/Common/Input';
import PasswordInput from 'components/Common/PasswordInput';
import { NetsapiensCsp } from 'apis/agency/telephony/types';
import { useEffect, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { RxCross2 } from 'react-icons/rx';
import { formatOutboundSipUris, normalizeOutboundSipUris } from '../utils/netsapiensCsp';

const cspSchema = z.object({
  csp_name: z.string().trim().min(1, 'CSP name is required'),
  api_url: z.string().trim().min(1, 'API URL is required'),
  portal_url: z.string().trim().min(1, 'Portal URL is required'),
  client_id: z.string().trim().min(1, 'Client ID is required'),
  client_secret: z.string().trim().min(1, 'Client secret is required'),
  outbound_sip_uri: z.string().trim().min(1, 'Outbound SIP URI is required')
});

type CspFormValues = z.infer<typeof cspSchema>;

interface Props {
  open: boolean;
  initialValues?: NetsapiensCsp | null;
  existingCspNames?: string[];
  isSaving?: boolean;
  onClose: () => void;
  onSave: (values: NetsapiensCsp) => void | Promise<void>;
}

function toFormValues(csp?: NetsapiensCsp | null): CspFormValues {
  return {
    csp_name: csp?.csp_name || '',
    api_url: csp?.api_url || '',
    portal_url: csp?.portal_url || '',
    client_id: csp?.client_id || '',
    client_secret: csp?.client_secret || '',
    outbound_sip_uri: formatOutboundSipUris(csp?.outbound_sip_uri)
  };
}

export default function NetsapiensCspDrawer({
  open,
  initialValues,
  existingCspNames = [],
  isSaving = false,
  onClose,
  onSave
}: Props) {
  const theme = useTheme();
  const isMediumScreen = useMediaQuery(theme.breakpoints.down('md'));
  const isEditing = Boolean(initialValues?.csp_name);
  const wasOpenRef = useRef(false);

  const { control, handleSubmit, reset, setError } = useForm<CspFormValues>({
    resolver: zodResolver(cspSchema),
    defaultValues: toFormValues()
  });

  useEffect(() => {
    if (open && !wasOpenRef.current) {
      reset(toFormValues(initialValues));
    }
    wasOpenRef.current = open;
  }, [open, initialValues, reset]);

  const submit = handleSubmit(async (values) => {
    if (existingCspNames.includes(values.csp_name.trim())) {
      setError('csp_name', { message: 'A CSP with this name already exists' });
      return;
    }

    await onSave({
      ...initialValues,
      ...values,
      csp_name: values.csp_name.trim(),
      api_url: values.api_url.trim(),
      portal_url: values.portal_url.trim(),
      client_id: values.client_id.trim(),
      client_secret: values.client_secret.trim(),
      outbound_sip_uri: normalizeOutboundSipUris(values.outbound_sip_uri)
    });
  });

  return (
    <Drawer
      anchor='right'
      open={open}
      onClose={onClose}
      sx={{
        '& .MuiDrawer-paper': {
          width: isMediumScreen ? '100%' : 480,
          maxWidth: '100%'
        }
      }}
    >
      <Box
        display='flex'
        alignItems='center'
        justifyContent='space-between'
        px={3}
        py={2}
        borderBottom='1px solid #ECECEC'
      >
        <Typography fontWeight={500} fontSize={18}>
          {isEditing ? 'Edit CSP' : 'Add CSP'}
        </Typography>
        <IconButton onClick={onClose} size='small'>
          <RxCross2 />
        </IconButton>
      </Box>

      <Box display='flex' flexDirection='column' height='100%'>
        <Stack spacing={2} px={3} py={3} flex={1}>
          <Input name='csp_name' label='CSP Name' control={control} fullWidth />
          <Input name='api_url' label='API URL' control={control} fullWidth placeholder='https://api.example.com' />
          <Input
            name='portal_url'
            label='Portal URL'
            control={control}
            fullWidth
            placeholder='https://portal.example.com'
          />
          <Input name='client_id' label='Client ID' control={control} fullWidth />
          <PasswordInput name='client_secret' label='Client Secret' control={control} fullWidth />
          <Input
            name='outbound_sip_uri'
            label='Outbound SIP URI'
            control={control}
            fullWidth
            placeholder={['sip.example.com', 'sip2.example.com'].join(', ')}
          />
        </Stack>

        <Box display='flex' justifyContent='flex-end' gap={1.2} px={3} py={2} borderTop='1px solid #ECECEC'>
          <Button
            variant='outlined'
            color='secondary'
            onClick={onClose}
            sx={{ borderRadius: '18px' }}
            disabled={isSaving}
          >
            Cancel
          </Button>
          <Button
            type='button'
            variant='contained'
            sx={{ borderRadius: '18px' }}
            disabled={isSaving}
            onClick={() => void submit()}
          >
            {isSaving ? 'Saving...' : isEditing ? 'Save Changes' : 'Add CSP'}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
